<template>
    <BCard class="substances-review-section mb-1">
        <template #header>
            <div class="substances-review-section__heading">
                <div>
                    <h5 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):substances', 'Substances') }}
                    </h5>
                    <p class="text-less-muted mb-0">
                        {{ translate(
                            'Component(ResearchReviewProcess):substances-description',
                            'Review extracted substance facts and confirm their Chemity matches.',
                        ) }}
                    </p>
                    <small class="text-less-muted d-block mt-25">
                        {{ summaryLabel }}
                    </small>
                </div>
                <BButton
                    type="button"
                    variant="outline-primary"
                    class="substances-review-section__add"
                    @click="addSubstance"
                >
                    <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-substance', 'Add substance') }}
                </BButton>
            </div>
        </template>

        <BAlert v-if="!viewModel.items.length" variant="light-secondary" show class="mb-0">
            <div class="alert-body">
                {{ translate('Component(ResearchReviewProcess):no-substances', 'No substances were extracted from this document.') }}
            </div>
        </BAlert>

        <BAlert
            v-if="viewModel.unassignedLinks.length"
            variant="light-warning"
            show
        >
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):unassigned-substance-links',
                    'Some existing substance matches are not associated with an extracted row. They are preserved but require review.',
                ) }}
            </div>
        </BAlert>

        <BAlert
            v-if="missingRegulationSubstanceIds.length"
            variant="light-warning"
            show
        >
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):missing-regulation-substances',
                    'Some linked Chemity substances no longer exist and could not be associated with regulations.',
                ) }}
            </div>
        </BAlert>

        <SubstanceReviewCard
            v-for="item in viewModel.items"
            ref="substanceCards"
            :key="item.rowKey"
            :number="item.index + 1"
            :substance-index="item.index"
            :substance="item.substance"
            :link="item.link"
            :summary="item.summary"
            :evaluation="item.evaluation"
            :hydrating="isHydrating(item.link)"
            :violations="substanceViolations(item.index)"
            @input="updateSubstance(item.index, $event)"
            @select-link="selectLink(item, $event)"
            @remove="removeSubstance(item.index)"
        />
    </BCard>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import SubstanceReviewCard from './SubstanceReviewCard.vue'
import substanceReviewCheckerRegistry from './substance-review-checker-registry'
import { buildSubstanceReviewViewModel, hasIdentity } from './substance-review-view-model'
import { substanceViolationTarget } from '../../validation/symfony-property-path'

const clone = value => JSON.parse(JSON.stringify(value || []))

const normalizeSummary = summary => ({
    id: summary.id,
    name: summary.name || summary.title || String(summary.id),
    title: summary.name || summary.title || String(summary.id),
    cas: summary.cas || '',
    ec: summary.ec || '',
    echaId: summary.echaId || '',
    raw: summary.raw || summary,
})

export default {
    name: 'SubstancesReviewSection',
    components: {
        BAlert,
        BButton,
        BCard,
        SubstanceReviewCard,
    },
    props: {
        substances: {
            type: Array,
            default: () => [],
        },
        links: {
            type: Array,
            default: () => [],
        },
        checkerRegistry: {
            type: Object,
            default: () => substanceReviewCheckerRegistry,
        },
        workspaceService: {
            type: Object,
            required: true,
        },
        missingRegulationSubstanceIds: {
            type: Array,
            default: () => [],
        },
        violations: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            summariesById: {},
            hydratingIds: [],
            hydrationRevision: 0,
        }
    },
    computed: {
        viewModel() {
            return buildSubstanceReviewViewModel(
                this.substances,
                this.links,
                this.summariesById,
                this.checkerRegistry,
            )
        },
        linkedSubstanceIds() {
            return Array.from(new Set(this.links
                .map(link => link && link.substanceId ? Number(link.substanceId) : null)
                .filter(id => Number.isFinite(id) && id > 0)))
        },
        linkedSubstanceIdsKey() {
            return this.linkedSubstanceIds.join(',')
        },
        summaryLabel() {
            const matched = this.viewModel.items.filter(item => (
                item.link && item.link.substanceId && item.link.status === 'accepted'
            )).length
            const template = this.translate(
                'Component(ResearchReviewProcess):substances-summary',
                '{total} extracted · {matched} matched',
            )

            return template
                .replace('{total}', String(this.viewModel.items.length))
                .replace('{matched}', String(matched))
        },
    },
    watch: {
        violations: {
            deep: true,
            handler() {
                this.focusFirstSubstanceViolation()
            },
        },
        linkedSubstanceIdsKey: {
            immediate: true,
            handler() {
                this.hydrateLinkedSubstances()
            },
        },
    },
    beforeDestroy() {
        this.hydrationRevision += 1
    },
    methods: {
        substanceViolations(index) {
            return this.violations.filter(violation => {
                const target = substanceViolationTarget(violation)

                return target && target.index === index
            })
        },
        focusFirstSubstanceViolation() {
            const target = this.violations
                .map(substanceViolationTarget)
                .find(Boolean)
            if (!target) {
                return
            }

            this.$nextTick(() => {
                const cards = Array.isArray(this.$refs.substanceCards)
                    ? this.$refs.substanceCards
                    : [this.$refs.substanceCards].filter(Boolean)
                const card = cards.find(item => item.substanceIndex === target.index)
                if (card && typeof card.focusViolation === 'function') {
                    card.focusViolation(target.field)
                }
            })
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        emitState({
            substances = this.substances,
            links = this.links,
        }) {
            this.$emit('update', {
                substances: clone(substances),
                links: clone(links),
            })
        },
        addSubstance() {
            this.emitState({
                substances: this.substances.concat({
                    extractedSubstanceId: uuidv4(),
                    name: '',
                    ecNumber: null,
                    casNumber: null,
                    concentration: null,
                }),
            })
        },
        updateSubstance(index, substance) {
            this.emitState({
                substances: this.substances.map((item, itemIndex) => (
                    itemIndex === index ? substance : item
                )),
            })
        },
        removeSubstance(index) {
            const substance = this.substances[index]
            const extractedSubstanceId = substance && substance.extractedSubstanceId
            const links = hasIdentity(extractedSubstanceId)
                ? this.links.filter(link => !link
                    || !hasIdentity(link.extractedSubstanceId)
                    || String(link.extractedSubstanceId) !== String(extractedSubstanceId))
                : this.links

            this.emitState({
                substances: this.substances.filter((item, itemIndex) => itemIndex !== index),
                links,
            })
        },
        selectLink(item, selected) {
            let extractedSubstanceId = item.substance.extractedSubstanceId
            let substances = this.substances

            if (!hasIdentity(extractedSubstanceId)) {
                extractedSubstanceId = uuidv4()
                substances = this.substances.map((substance, index) => (
                    index === item.index
                        ? { ...substance, extractedSubstanceId }
                        : substance
                ))
            }

            if (!selected || !selected.id) {
                const links = item.linkIndex === null
                    ? this.links
                    : this.links.filter((link, index) => index !== item.linkIndex)
                this.emitState({ substances, links })
                return
            }

            const substanceId = Number(selected.id)
            if (!Number.isFinite(substanceId) || substanceId <= 0) {
                return
            }

            this.$set(this.summariesById, String(substanceId), normalizeSummary(selected))
            const nextLink = {
                ...(item.link || {}),
                extractedSubstanceId,
                substanceId,
                type: 'manual',
                status: 'accepted',
                confidence: null,
                reason: null,
            }
            const links = item.linkIndex === null
                ? this.links.concat(nextLink)
                : this.links.map((link, index) => index === item.linkIndex ? nextLink : link)

            this.emitState({ substances, links })
        },
        isHydrating(link) {
            return !!(link && link.substanceId && this.hydratingIds.includes(Number(link.substanceId)))
        },
        hydrateLinkedSubstances() {
            const ids = this.linkedSubstanceIds.filter(id => !this.summariesById[String(id)])
            const revision = ++this.hydrationRevision

            if (!ids.length || typeof this.workspaceService.getSubstanceSummaries !== 'function') {
                this.hydratingIds = []
                return Promise.resolve()
            }

            this.hydratingIds = ids
            return this.workspaceService.getSubstanceSummaries(ids)
                .then((result) => {
                    if (revision !== this.hydrationRevision) {
                        return
                    }

                    const items = Array.isArray(result.items) ? result.items : []
                    items.forEach(summary => {
                        if (summary && hasIdentity(summary.id)) {
                            this.$set(this.summariesById, String(summary.id), normalizeSummary(summary))
                        }
                    })
                })
                .finally(() => {
                    if (revision === this.hydrationRevision) {
                        this.hydratingIds = []
                    }
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.substances-review-section__heading {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.substances-review-section__add {
    flex: 0 0 auto;
    margin-left: auto;
}

@media (max-width: 767.98px) {
    .substances-review-section__heading {
        flex-direction: column;
    }

    .substances-review-section__add {
        margin-left: 0;
    }
}
</style>
